/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { FormControl, IconButton, InputAdornment, InputLabel, Select } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import { StyledMenuItem } from "../styled_menu_item";
import { SelectedMenuItem } from "../selected_menu_item";
import { selectBaseStyles } from "../../pages/reports/styles";

export const CARD_SOURCE_ALL = 'all';

export const CARD_SOURCE_OPTIONS = [
  { value: CARD_SOURCE_ALL, label: 'All', description: 'Owned and shared cards' },
  { value: 'owned', label: 'Owned', description: 'Cards the group issued' },
  { value: 'shared', label: 'Shared', description: 'Cards another group issued and this group handed out' },
];

// Request params for the filter. "All" sends nothing, so the default request stays unchanged.
export const cardSourceParams = (cardSource) => (
  cardSource && cardSource !== CARD_SOURCE_ALL ? { card_source: cardSource } : {}
);

export const CARD_SOURCE_NEEDS_GROUP = 'Select a restaurant group to filter by card source';

// Card source filter for the card-based Perq reports. Pages disable it until a group is selected.
export const CardSourceFilter = ({ value = CARD_SOURCE_ALL, onChange, disabled = false }) => {
  const id = React.useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const selected = CARD_SOURCE_OPTIONS.find((option) => option.value === value) || CARD_SOURCE_OPTIONS[0];

  return (
    <FormControl
      size="small"
      data-testid="card-source-filter"
      disabled={disabled}
      title={disabled ? CARD_SOURCE_NEEDS_GROUP : undefined}
    >
      <InputLabel id={labelId} sx={{ fontSize: '14px' }}>Card Source</InputLabel>
      <Select
        labelId={labelId}
        // Tells screen readers why the select is disabled.
        SelectDisplayProps={disabled ? { 'aria-describedby': hintId } : undefined}
        value={selected.value}
        renderValue={() => selected.label}
        size="small"
        label="Card Source"
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          // Wider than the select so the selected tick isn't clipped.
          PaperProps: { sx: { width: '220px' } },
        }}
        sx={{
          ...selectBaseStyles,
          minWidth: 120,
          width: 140,
          borderColor: "#2b4fb9",
        }}
        onChange={(event) => onChange(event.target.value)}
        endAdornment={
          selected.value !== CARD_SOURCE_ALL && !disabled ? (
            <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
              <IconButton
                edge='end'
                size="small"
                disableRipple
                aria-label="Clear card source"
                onClick={() => onChange(CARD_SOURCE_ALL)}
              >
                <ClearIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      >
        {CARD_SOURCE_OPTIONS.map((option) => (
          <StyledMenuItem key={option.value} value={option.value} title={option.description}>
            {selected.value === option.value ? <SelectedMenuItem data={option.label} /> : option.label}
          </StyledMenuItem>
        ))}
      </Select>
      {disabled && <span id={hintId} hidden>{CARD_SOURCE_NEEDS_GROUP}</span>}
    </FormControl>
  );
};
